import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewCommunicationCompliance = {

    id: 49,
    slug: 'purview-communication-compliance',
    ogImage: '/og/courses/purview-communication-compliance.jpg',
    courseCode: 'PURVIEW-CC-001',
    featured: false,
    title: 'Microsoft Purview Communication Compliance',
    subtitle: 'Detect and review risky communications across Teams, Exchange, and Viva Engage',
    organization: 'EDUC4TE',
    valueProposition: 'Stand up a defensible Communication Compliance programme in Microsoft Purview that detects harassment, regulatory, code-of-conduct, and human-to-agent risk, with reviewer workflows that satisfy APP 11, APRA CPS 234, and Fair Work procedural fairness. This elective fills the Communication Compliance surface SC-401 no longer covers.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_4,
    shortDescription: 'PURVIEW-CC-001 — Purview Communication Compliance covering policy design, reviewer workflows, and Fair Work fairness for HR, legal, and compliance teams.',
    price: {
      aud: 1000,
      currency: 'AUD'
    },
    stripeConfig: {
      priceId: 'price_1Te5C1Ku1nyc322UXhpdsRjZ',
      productId: 'prod_UdLtiZesgBxweJ'
    },

    snapshot: {
      whoItsFor: [
        'Compliance analysts operating Microsoft Purview Communication Compliance across Teams, Exchange, and Viva Engage',
        'Joint HR and IT teams responsible for employee communication monitoring under the Fair Work Act',
        'Regulated-industry compliance officers in financial services and healthcare with communication-supervision obligations',
        'Legal and employee-relations teams preparing OAIC APP 11 and APRA CPS 234 defensibility evidence'
      ],
      accreditation: 'Aligned to Microsoft Purview Communication Compliance learning paths. Fills the gap left by the exclusion of Communication Compliance from the SC-401 associate exam.'
    },

    logistics: {
      classSize: 'One-day intensive — virtual cohort with hands-on labs, joint HR/IT scenarios, and live Q&A',
      requirements: [
        'Working knowledge of Microsoft 365 (Exchange, Teams, Viva Engage)',
        'Familiarity with the Microsoft Purview portal and role groups',
        'Access to a Microsoft 365 E5 (or Compliance add-on) tenant for labs',
        'Awareness of Australian privacy and employee-monitoring obligations',
        'HR partnership and stakeholder sign-off for communication monitoring activities'
      ]
    },

    overview: 'Microsoft Purview Communication Compliance is a one-day elective that takes compliance, HR, legal, and security practitioners from policy design through to a working reviewer operating model that holds up under OAIC investigation, APRA CPS 234 review, and Fair Work scrutiny. You will design Communication Compliance policies for harassment, regulatory, and code-of-conduct scenarios across Teams, Exchange, and Viva Engage; configure classifiers and conditions for risky communications; operate defensible reviewer workflows, escalation paths, and sample-and-investigate patterns; review human-to-agent interactions — capturing Copilot and agent conversations under the same defensibility standard as human exchanges; and reconcile APP 11 employee privacy with lawful monitoring under the Fair Work Act and state surveillance legislation. The programme is grounded in Microsoft Learn guidance updated through 2025 and 2026, OAIC Australian Privacy Principles guidance, and the procedural fairness requirements of the Fair Work Act. It fills the Communication Compliance gap the SC-401 associate exam no longer covers.',

    outcomes: [
      'Design Communication Compliance policies for harassment, regulatory, and code-of-conduct scenarios across Teams, Exchange, and Viva Engage',
      'Configure classifiers and conditions to detect risky communications',
      'Operate defensible reviewer workflows and escalation paths that satisfy procedural fairness',
      'Review human-to-agent (Copilot/agent) interactions to the same defensibility standard as human exchanges',
      'Reconcile APP 11 employee privacy with lawful monitoring under the Fair Work Act and state surveillance legislation',
      'Produce a privacy-by-design defensibility evidence pack for OAIC and APRA review'
    ],

    audience: [
      'Communication compliance analysts and reviewers',
      'Joint HR and IT compliance teams',
      'Regulated-industry compliance officers (financial services, healthcare)',
      'Legal, privacy, and employee-relations teams'
    ],

    prerequisites: [
      'HR partnership and stakeholder sign-off for communication monitoring',
      'Access to a Microsoft 365 E5 (or Compliance add-on) tenant',
      'Familiarity with the Microsoft Purview portal and role groups',
      'Awareness of Australian privacy law and Fair Work obligations'
    ],

    modules: [
      {
        number: 1,
        title: 'Communication Compliance foundations',
        duration: '2 hours',
        topics: [
          'Policy templates for harassment, regulatory (financial services, healthcare), and code-of-conduct scenarios',
          'Supported locations: Microsoft Teams, Exchange, and Viva Engage',
          'Classifiers and conditions for detecting risky communications',
          'Role-based access design for reviewers, analysts, investigators, and viewers',
          'Privacy-by-design defaults: anonymised pseudonyms and minimum-necessary scoping'
        ]
      },
      {
        number: 2,
        title: 'Reviewer workflows and investigations',
        duration: '3 hours',
        topics: [
          'Reviewer workflows, escalation paths, and sample-and-investigate patterns',
          'Triaging flagged communications and documenting reviewer decisions',
          'Procedural fairness under the Fair Work Act and state surveillance legislation',
          'Human-to-agent review: capturing Copilot and agent conversations under the same standard as human exchanges',
          'Escalation to eDiscovery for matters requiring legal hold'
        ]
      },
      {
        number: 3,
        title: 'Defensibility and regulatory alignment',
        duration: '3 hours',
        topics: [
          'APP 11 employee-privacy obligations versus lawful monitoring',
          'APRA CPS 234 communication-supervision and information-security requirements',
          'Privacy-by-design evidence and audit trails from policy match to resolution',
          'Building an OAIC defensibility pack for regulatory review',
          'Reference: Microsoft Learn — Communication Compliance (updated 2025/2026)'
        ]
      }
    ],

    format: {
      type: 'One-day intensive (virtual)',
      includes: [
        '8 hours of instructor-led training across one day',
        'Hands-on labs in a Microsoft 365 compliance tenant with Communication Compliance enabled',
        'Joint HR and IT scenario walkthroughs covering procedural fairness and reviewer dual-authorisation',
        'Reviewer-workflow playbook covering sample-and-investigate and escalation paths',
        'Privacy-by-design defensibility evidence pack template for OAIC and APRA review',
        'Human-to-agent (Copilot/agent) review walkthrough',
        'Certificate of completion suitable for CPD claims'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Register interest for the next one-day intensive — small cohort to support hands-on labs and joint HR/IT scenarios'
    },

    certification: 'Educ4te Certificate of Completion — Microsoft Purview Communication Compliance. Fills the curriculum gap left by the exclusion of Communication Compliance from the SC-401 associate exam.',

    faqs: [
      {
        question: 'Why is Communication Compliance not covered by SC-401?',
        answer: 'Microsoft retired SC-400 and replaced it with the SC-401 Administering Information Security in Microsoft 365 associate exam, which excludes Communication Compliance from its objectives. This elective exists to preserve the depth on Communication Compliance that the current Microsoft official curriculum no longer delivers.'
      },
      {
        question: 'Why does the course insist on HR partnership before deployment?',
        answer: 'Communication Compliance monitors employee communications. Under the Fair Work Act, state surveillance legislation, and APP 11, monitoring employee communications requires a lawful basis, notice, procedural fairness, and (in most cases) HR co-ownership. Deploying without HR sign-off creates legal exposure and undermines the defensibility of any subsequent review.'
      },
      {
        question: 'Do I need a Microsoft 365 E5 tenant to follow the labs?',
        answer: 'Yes — Communication Compliance requires a Microsoft 365 E5 licence or the Compliance add-on. A developer tenant with the Compliance add-on is sufficient for the labs.'
      },
      {
        question: 'How does the course handle review of human-to-agent (Copilot/agent) interactions?',
        answer: 'As Copilot and agent conversations carry the same harassment, regulatory, and code-of-conduct risk as human exchanges, the course shows how to bring human-to-agent interactions into Communication Compliance policies and review them to the same defensibility standard — including the evidence and audit trail expected for a regulated communication channel.'
      },
      {
        question: 'How does the course address Fair Work Act employee-monitoring constraints?',
        answer: 'Every monitoring control is paired with the lawful basis, notice, and procedural fairness pattern required under the Fair Work Act and applicable state surveillance legislation. We walk through anonymised reviewer workflows, sample-and-investigate patterns, escalation paths, and the documentation an employee-relations team needs if a matter escalates. This is the APP 11 versus Fair Work and state surveillance tension resolved in practice.'
      },
      {
        question: 'Is the course eligible for CPD?',
        answer: 'Yes — the 8 hours of instructor-led training and accompanying evidence pack are suitable for CPD claims with professional bodies including ACS, IAPP, AISA, and the Governance Institute of Australia. A certificate of completion is issued on successful participation.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['Privacy Act 1988', 'OAIC APP 11', 'APRA CPS 234', 'Fair Work Act']
};
